import config from 'config';
import { format } from 'url';
import { sanitizePayload } from '~/analytics/payload';
import { EVENT_TYPES } from '~/analytics/events';
import searchExperienceContextSelector from '~/analytics/contexts/selectors/searchExperience';
import {
  actions as assetsActions,
  selectors as assetsSelectors
} from '~/domains/assets';
import {
  actions as commonActions,
  selectors as commonSelectors
} from '~/domains/common';
import {
  actions as searchActions,
  selectors as searchSelectors
} from '~/domains/search';
import ANGFilters from '~/utils/ang/ANGFilters';
import * as arrayUtils from '~/utils/arrays';
import { parseSearch } from '~/utils/location';
import { isClient } from '~/utils/window';

const resolve = async ({
  dispatch,
  getState,
  location,
  helpers: { trackEvent, assetTypesConfiguration }
}) => {
  const state = getState();
  const query = parseSearch(location.search);
  const profile = commonSelectors.profile(state);
  const isANGSearchEnabled = searchSelectors.isANGSearchEnabled(state);
  const isSearchTrigger = commonSelectors.hasHomeQueryChanged(state, {
    query
  });
  const promises = [];
  const isClientSide = isClient();
  const isSearchPermitted =
    !isANGSearchEnabled || !isSearchTrigger || isClientSide;
  const shouldExecuteANGSearch =
    isSearchTrigger && isANGSearchEnabled && isClientSide;
  let rootOrganizations;

  if (isANGSearchEnabled) {
    rootOrganizations = await dispatch(searchActions.getRootOrganizations());

    promises.push(
      dispatch(
        searchActions.getFacets(
          query,
          assetTypesConfiguration,
          rootOrganizations
        )
      )
    );
  }

  if (isSearchTrigger && !isANGSearchEnabled) {
    promises.push(
      dispatch(assetsActions.getAssets({ query, assetTypesConfiguration }))
    );
  } else if (shouldExecuteANGSearch) {
    const newState = getState();
    const boostConfiguration = ANGFilters.getBoostConfiguration(query);
    const angConfig = searchSelectors.angConfig(newState, {
      sort: query.sort,
      show: query.show,
      search: query.search,
      boostConfiguration,
      assetTypesConfiguration,
      includeEmptyFilters: true
    });
    const filters = ANGFilters.buildFromQuery(
      query,
      assetTypesConfiguration,
      profile,
      rootOrganizations
    );

    promises.push(dispatch(searchActions.search(filters, angConfig)));
  }

  if (isSearchPermitted) {
    dispatch(commonActions.setHomeQuery(query));
    dispatch(
      commonActions.setHeader(buildHeader(query, assetTypesConfiguration))
    );
  }

  await Promise.all(promises);

  if (isSearchPermitted && isSearchTrigger) {
    trackSearchExecuted({
      getState,
      trackEvent,
      query,
      assetTypesConfiguration
    });
  }
};

export function trackSearchExecuted({
  getState,
  trackEvent,
  query = {},
  assetTypesConfiguration
}) {
  let angSearchProps = null;
  const newState = getState();
  const assets = assetsSelectors.listAssets(newState);
  const isANGSearchEnabled = searchSelectors.isANGSearchEnabled(newState);
  const resultCount = assets.length;

  if (isANGSearchEnabled) {
    angSearchProps = getANGSearchProperties(assets);
  }

  trackEvent(EVENT_TYPES.SEARCH_EXECUTED, {
    query: sanitizePayload(query),
    resultCount,
    ...angSearchProps
  });
  if (isANGSearchEnabled) {
    trackFilterEvents(trackEvent, query, newState, assetTypesConfiguration);
  }
}

function trackFilterEvents(
  trackEvent,
  query,
  newState,
  assetTypesConfiguration
) {
  const context = searchExperienceContextSelector(newState);
  const responseTime = searchSelectors.facetsResponseTime(newState);
  const filters = {
    categories: searchSelectors.categoriesFilter(newState),
    types: searchSelectors.typesFilter(newState, { assetTypesConfiguration }),
    tags: searchSelectors.tagsFilter(newState),
    organizations: searchSelectors
      .organizations(newState)
      .map(({ id, isMaster }) => ({ id, isMaster }))
  };

  Object.keys(filters).forEach((filter) => {
    if (filters[filter] && filters[filter].length) {
      trackEvent(EVENT_TYPES.SEARCH_FILTER_POPULATED, {
        context,
        filter,
        query: sanitizePayload(query),
        count: filters[filter].length,
        values: filters[filter],
        responseTime
      });
    }
  });
}

function getANGSearchProperties(assets) {
  if (assets.length === 0) {
    return null;
  }

  const totalMinorCount = assets.reduce(
    (accum, asset) => accum + asset.highlight.scores.count,
    0
  );
  const matchedMinorCountAverage = totalMinorCount / assets.length;

  const updatedOutsideBatchCount = assets.reduce(
    (accum, asset) => (asset.replacedOutsideBatch ? accum + 1 : accum),
    0
  );
  const updatedOutsideBatchRatio = updatedOutsideBatchCount / assets.length;

  const latestMatchedMinorHighestScoreCount = assets.reduce((accum, asset) => {
    const { highlight, score } = asset;
    const { scores } = highlight;

    return score === scores.latestVersionScore ? accum + 1 : accum;
  }, 0);
  const latestMatchedMinorHighestScorePercentage =
    (latestMatchedMinorHighestScoreCount / assets.length) * 100;

  return {
    updatedOutsideBatchCount,
    updatedOutsideBatchRatio,
    matchedMinorCountAverage,
    latestMatchedMinorHighestScoreCount,
    latestMatchedMinorHighestScorePercentage
  };
}

export function buildHeader(query = {}, assetTypesConfiguration) {
  const queryTypes = arrayUtils.convertToArray(query.type);
  const typesNames = queryTypes
    .reduce((accum, current) => {
      const type = assetTypesConfiguration[current];

      if (!type) {
        return accum;
      }

      const name = type.filter;

      if (accum.includes(name)) {
        return accum;
      }

      return [...accum, name];
    }, [])
    .join(', ');

  return {
    title: typesNames.length ? `${typesNames} - ${config.title}` : config.title,
    description: config.description.replace(
      // eslint-disable-next-line no-template-curly-in-string
      '${ASSETS}',
      typesNames.length ? `${typesNames}` : 'assets'
    ),
    canonical: format({ ...config.mulesoft.site, query: { type: query.type } })
  };
}

export default resolve;
